import http from './http';

/**
 * Accounting API Configuration and Services
 * Manages all accounting-related API calls
 */

// Chart of Accounts API Service
class ChartOfAccountsService {
  constructor() {
    this.endpoint = '/api/v1/accounting/chart-of-accounts';
  }

  async getAll(params = {}) {
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 50,
      search: params.search || '',
      isActive: params.isActive !== undefined ? params.isActive : true,
      accountType: params.accountType || '',
      accountCategory: params.accountCategory || '',
      sortBy: params.sortBy || 'accountCode',
      sortOrder: params.sortOrder || 'asc'
    };

    const response = await http.get(this.endpoint, { params: queryParams });
    return response.data.data;
  }

  async getById(id) {
    const response = await http.get(`${this.endpoint}/${id}`);
    return response.data.data;
  }

  async create(data) {
    const response = await http.post(this.endpoint, data);
    return response.data.data;
  }

  async update(id, data) {
    const response = await http.put(`${this.endpoint}/${id}`, data);
    return response.data.data;
  }

  async delete(id) {
    const response = await http.delete(`${this.endpoint}/${id}`);
    return response.data.data;
  }

  async getHierarchy(id) {
    const response = await http.get(`${this.endpoint}/${id}/hierarchy`);
    return response.data.data;
  }

  async getAccountsByType(type) {
    const response = await http.get(`${this.endpoint}/type/${type}`);
    return response.data.data;
  }

  async getAccountsForStatement(statement) {
    const response = await http.get(`${this.endpoint}/statement/${statement}`);
    return response.data.data;
  }

  async initializeDefaultAccounts() {
    const response = await http.post(`${this.endpoint}/initialize`);
    return response.data.data;
  }
}

// Bank Master API Service
class BankMasterService {
  constructor() {
    this.endpoint = '/api/v1/accounting/bank-accounts';
  }

  async getAll(params = {}) {
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 50,
      search: params.search || '',
      accountType: params.accountType || '',
      status: params.status || '',
      sortBy: params.sortBy || 'bankName',
      sortOrder: params.sortOrder || 'asc'
    };

    const response = await http.get(this.endpoint, { params: queryParams });
    return response.data.data;
  }

  async getById(id) {
    const response = await http.get(`${this.endpoint}/${id}`);
    return response.data.data;
  }

  async create(data) {
    const response = await http.post(this.endpoint, data);
    return response.data.data;
  }

  async update(id, data) {
    const response = await http.put(`${this.endpoint}/${id}`, data);
    return response.data.data;
  }

  async delete(id) {
    const response = await http.delete(`${this.endpoint}/${id}`);
    return response.data.data;
  }

  async getDefaultAccount(accountType) {
    const response = await http.get(`${this.endpoint}/default/${accountType}`);
    return response.data.data;
  }

  async updateAccountBalance(id, balanceData) {
    const response = await http.post(`${this.endpoint}/${id}/balance`, balanceData);
    return response.data.data;
  }

  async getAccountBalance(id) {
    const response = await http.get(`${this.endpoint}/${id}/balance`);
    return response.data.data;
  }

  async getAccountsByType(accountType) {
    const response = await http.get(`${this.endpoint}/type/${accountType}`);
    return response.data.data;
  }

  async linkChartOfAccount(id, chartOfAccountId) {
    const response = await http.put(`${this.endpoint}/${id}/link-chart-account`, { chartOfAccountId });
    return response.data.data;
  }

  async unlinkChartOfAccount(id) {
    const response = await http.delete(`${this.endpoint}/${id}/link-chart-account`);
    return response.data.data;
  }

  async uploadDocuments(id, documents) {
    const response = await http.post(`${this.endpoint}/${id}/documents`, documents);
    return response.data.data;
  }
}

// Vendor Master API Service
class VendorMasterService {
  constructor() {
    this.endpoint = '/api/v1/vendors';
  }

  /**
   * Get vendor statistics
   * @returns {Promise} Statistics about vendors
   */
  async getStatistics(params = {}) {
    const queryParams = {
      vendorType: params.vendorType || '',
      status: params.status || ''
    };

    const response = await http.get(`${this.endpoint}/statistics`, { params: queryParams });
    return response.data.data;
  }

  /**
   * Get all vendors with pagination and filters
   * @param {Object} params - Query parameters
   * @returns {Promise} Paginated list of vendors
   */
  async getAll(params = {}) {
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 50,
      search: params.search || '',
      status: params.status || '',
      vendorType: params.vendorType || '',
      sortBy: params.sortBy || 'vendorName',
      sortOrder: params.sortOrder || 'asc'
    };

    const response = await http.get(this.endpoint, { params: queryParams });
    return response.data;
  }

  /**
   * Get vendor by ID
   * @param {string} id - Vendor ID
   * @returns {Promise} Vendor details
   */
  async getById(id) {
    const response = await http.get(`${this.endpoint}/${id}`);
    return response.data.data;
  }

  /**
   * Create a new vendor
   * @param {Object} data - Vendor data
   * @returns {Promise} Created vendor
   */
  async create(data) {
    const response = await http.post(this.endpoint, data);
    return response.data.data;
  }

  /**
   * Update vendor
   * @param {string} id - Vendor ID
   * @param {Object} data - Updated vendor data
   * @returns {Promise} Updated vendor
   */
  async update(id, data) {
    const response = await http.put(`${this.endpoint}/${id}`, data);
    return response.data.data;
  }

  /**
   * Delete or deactivate vendor
   * @param {string} id - Vendor ID
   * @param {boolean} permanent - Whether to permanently delete
   * @returns {Promise} Deletion result
   */
  async delete(id, permanent = false) {
    const response = await http.delete(`${this.endpoint}/${id}`, {
      params: { permanent }
    });
    return response.data;
  }

  /**
   * Get vendor ledger entries
   * @param {string} id - Vendor ID
   * @param {Object} params - Query parameters
   * @returns {Promise} Vendor ledger entries
   */
  async getLedger(id, params = {}) {
    const queryParams = {
      startDate: params.startDate || '',
      endDate: params.endDate || '',
      page: params.page || 1,
      limit: params.limit || 50
    };

    const response = await http.get(`${this.endpoint}/${id}/ledger`, { params: queryParams });
    return response.data;
  }

  /**
   * Get vendor payment history
   * @param {string} id - Vendor ID
   * @param {Object} params - Query parameters
   * @returns {Promise} Vendor payment history
   */
  async getPayments(id, params = {}) {
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 50
    };

    const response = await http.get(`${this.endpoint}/${id}/payments`, { params: queryParams });
    return response.data;
  }
}

// Expense Master API Service (placeholder - will be implemented when backend is ready)
class ExpenseMasterService {
  constructor() {
    this.endpoint = '/api/v1/accounting/expenses';
  }

  async getAll(params = {}) {
    return { records: [], pagination: { total: 0, totalCount: 0 } };
  }

  async getById(id) {
    return null;
  }

  async create(data) {
    return data;
  }

  async update(id, data) {
    return data;
  }

  async delete(id) {
    return { success: true };
  }
}

// Fixed Asset Master API Service (placeholder - will be implemented when backend is ready)
class FixedAssetMasterService {
  constructor() {
    this.endpoint = '/api/v1/accounting/fixed-assets';
  }

  async getAll(params = {}) {
    return { records: [], pagination: { total: 0, totalCount: 0 } };
  }

  async getById(id) {
    return null;
  }

  async create(data) {
    return data;
  }

  async update(id, data) {
    return data;
  }

  async delete(id) {
    return { success: true };
  }
}

// Transaction API Service (placeholder - will be implemented when backend is ready)
class TransactionService {
  constructor() {
    this.endpoint = '/api/v1/accounting/transactions';
  }

  async getAll(params = {}) {
    return { records: [], pagination: { total: 0, totalCount: 0 } };
  }

  async getById(id) {
    return null;
  }

  async create(data) {
    return data;
  }

  async update(id, data) {
    return data;
  }

  async delete(id) {
    return { success: true };
  }
}

// Reports API Service (placeholder - will be implemented when backend is ready)
class ReportsService {
  constructor() {
    this.endpoint = '/api/v1/accounting/reports';
  }

  async getBalanceSheet(params = {}) {
    return { assets: [], liabilities: [], equity: [] };
  }

  async getProfitAndLoss(params = {}) {
    return { income: [], expenses: [] };
  }

  async getCashFlow(params = {}) {
    return { operating: [], investing: [], financing: [] };
  }

  async getTrialBalance(params = {}) {
    return { accounts: [] };
  }
}

// Export service instances
export const chartOfAccountsService = new ChartOfAccountsService();
export const bankMasterService = new BankMasterService();
export const vendorMasterService = new VendorMasterService();
export const expenseMasterService = new ExpenseMasterService();
export const fixedAssetMasterService = new FixedAssetMasterService();
export const transactionService = new TransactionService();
export const reportsService = new ReportsService();

// Account Type and Category Constants
export const ACCOUNT_TYPES = {
  ASSETS: 'assets',
  LIABILITIES: 'liabilities',
  EQUITY: 'equity',
  INCOME: 'income',
  EXPENSES: 'expenses'
};

export const ACCOUNT_CATEGORIES = {
  // Asset Categories
  CURRENT_ASSETS: 'current_assets',
  FIXED_ASSETS: 'fixed_assets',
  INTANGIBLE_ASSETS: 'intangible_assets',
  
  // Liability Categories
  CURRENT_LIABILITIES: 'current_liabilities',
  LONG_TERM_LIABILITIES: 'long_term_liabilities',
  
  // Equity Categories
  CAPITAL: 'capital',
  RETAINED_EARNINGS: 'retained_earnings',
  
  // Income Categories
  OPERATING_INCOME: 'operating_income',
  OTHER_INCOME: 'other_income',
  
  // Expense Categories
  OPERATING_EXPENSES: 'operating_expenses',
  ADMINISTRATIVE_EXPENSES: 'administrative_expenses',
  FINANCIAL_EXPENSES: 'financial_expenses',
  DEPRECIATION: 'depreciation'
};

export const BANK_ACCOUNT_TYPES = {
  SAVINGS: 'savings',
  CURRENT: 'current',
  OVERDRAFT: 'overdraft',
  FIXED_DEPOSIT: 'fixed_deposit',
  CASH: 'cash',
  GPAY: 'gpay',
  UPI: 'upi',
  CREDIT_CARD: 'credit_card'
};

export const BANK_ACCOUNT_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  CLOSED: 'closed',
  FROZEN: 'frozen'
};

export const VENDOR_TYPES = {
  SUPPLIER: 'supplier',
  SERVICE_PROVIDER: 'service_provider',
  CONTRACTOR: 'contractor',
  CONSULTANT: 'consultant',
  MEDICINE_SUPPLIER: 'medicine_supplier',
  EQUIPMENT_SUPPLIER: 'equipment_supplier',
  UTILITY_PROVIDER: 'utility_provider'
};

export const VENDOR_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  BLACKLISTED: 'blacklisted',
  PENDING_APPROVAL: 'pending_approval'
};

export const PAYMENT_TERMS = {
  IMMEDIATE: 'immediate',
  NET_15: 'net_15',
  NET_30: 'net_30',
  NET_45: 'net_45',
  NET_60: 'net_60',
  ADVANCE_PAYMENT: 'advance_payment',
  CREDIT: 'credit'
};

export const GST_REGISTRATION_TYPES = {
  REGULAR: 'regular',
  COMPOSITION: 'composition',
  UNREGISTERED: 'unregistered',
  EXEMPTED: 'exempted'
};

export const ADDRESS_TYPES = {
  BILLING: 'billing',
  SHIPPING: 'shipping',
  REGISTERED: 'registered',
  BRANCH: 'branch'
};

export const REPORT_CLASSIFICATIONS = {
  BALANCE_SHEET: 'balance_sheet',
  PROFIT_AND_LOSS: 'profit_and_loss',
  CASH_FLOW: 'cash_flow'
};

// Helper functions for dropdowns
export const getAccountTypeOptions = () => {
  return Object.entries(ACCOUNT_TYPES).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }));
};

export const getAccountCategoryOptions = (accountType) => {
  const categoryMap = {
    [ACCOUNT_TYPES.ASSETS]: [
      ACCOUNT_CATEGORIES.CURRENT_ASSETS,
      ACCOUNT_CATEGORIES.FIXED_ASSETS,
      ACCOUNT_CATEGORIES.INTANGIBLE_ASSETS
    ],
    [ACCOUNT_TYPES.LIABILITIES]: [
      ACCOUNT_CATEGORIES.CURRENT_LIABILITIES,
      ACCOUNT_CATEGORIES.LONG_TERM_LIABILITIES
    ],
    [ACCOUNT_TYPES.EQUITY]: [
      ACCOUNT_CATEGORIES.CAPITAL,
      ACCOUNT_CATEGORIES.RETAINED_EARNINGS
    ],
    [ACCOUNT_TYPES.INCOME]: [
      ACCOUNT_CATEGORIES.OPERATING_INCOME,
      ACCOUNT_CATEGORIES.OTHER_INCOME
    ],
    [ACCOUNT_TYPES.EXPENSES]: [
      ACCOUNT_CATEGORIES.OPERATING_EXPENSES,
      ACCOUNT_CATEGORIES.ADMINISTRATIVE_EXPENSES,
      ACCOUNT_CATEGORIES.FINANCIAL_EXPENSES,
      ACCOUNT_CATEGORIES.DEPRECIATION
    ]
  };

  const categories = categoryMap[accountType] || [];
  return categories.map(category => ({
    value: category,
    label: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }));
};

export const getBankAccountTypeOptions = () => {
  return Object.entries(BANK_ACCOUNT_TYPES).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }));
};

export const getBankAccountStatusOptions = () => {
  return Object.entries(BANK_ACCOUNT_STATUS).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }));
};

export const getVendorTypeOptions = () => {
  return Object.entries(VENDOR_TYPES).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }));
};

export const getVendorStatusOptions = () => {
  return Object.entries(VENDOR_STATUS).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }));
};

export const getPaymentTermsOptions = () => {
  return Object.entries(PAYMENT_TERMS).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }));
};

export const getGstRegistrationTypeOptions = () => {
  return Object.entries(GST_REGISTRATION_TYPES).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }));
};

export const getAddressTypeOptions = () => {
  return Object.entries(ADDRESS_TYPES).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }));
};

export default {
  chartOfAccountsService,
  bankMasterService,
  vendorMasterService,
  expenseMasterService,
  fixedAssetMasterService,
  transactionService,
  reportsService,
  ACCOUNT_TYPES,
  ACCOUNT_CATEGORIES,
  BANK_ACCOUNT_TYPES,
  BANK_ACCOUNT_STATUS,
  VENDOR_TYPES,
  VENDOR_STATUS,
  PAYMENT_TERMS,
  GST_REGISTRATION_TYPES,
  ADDRESS_TYPES,
  REPORT_CLASSIFICATIONS
};